import React from "react";
import styled from "styled-components";
import { tokens } from "../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../breakpoints";

const StyledBar = styled.div({
    width: "100%",
    alignSelf: "stretch",
    padding: `${tokens.globals["spacing-5"]} ${tokens.globals["spacing-4"]}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: tokens.globals["spacing-4"],
    flexWrap: "wrap",
});

const StyledRow = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    flexWrap: "wrap",
    minWidth: 0,
});

const StyledSearch = styled.div(({ $disabled = false }) => ({
    width: "166px",
    minHeight: "35px",
    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-3"]}`,
    background: $disabled ? tokens.colors.neutral.grey["100"] : tokens.colors.surfaces["container-background"],
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-3"],
    opacity: $disabled ? 0.6 : 1,

    "&:focus-within": $disabled ? {} : {
        borderColor: tokens.colors.brand.blue["500"],
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "-1px",
    },

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        width: "100%",
    },
}));

const StyledSearchIcon = styled.i({
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    flexShrink: 0,
});

/*
 * Occupies the same slot as StyledSearchIcon (same size/color, no padding or border) so swapping
 * between the two never changes StyledSearch's box dimensions.
 */
const StyledSearchClearButton = styled.button({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    background: "transparent",
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["md-12px"],
    flexShrink: 0,
    cursor: "pointer",

    "&:hover": { color: tokens.colors.brand.blue["500"] },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },
});

const StyledSearchInput = styled.input({
    flex: "1 1 0",
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],

    "&::placeholder": {
        color: tokens.colors.text.secondary,
    },

    "&:disabled": {
        cursor: "not-allowed",
    },
});

const StyledFilterButton = styled.button(({ $active = false }) => ({
    height: "35px",
    padding: `0 ${tokens.globals["spacing-2-5"]}`,
    background: tokens.colors.surfaces["container-background"],
    boxShadow: tokens.effects.effects.shadow.default,
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${$active ? tokens.colors.brand.blue["500"] : tokens.colors.surfaces["border-divider"]}`,
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    cursor: "pointer",
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    whiteSpace: "nowrap",

    "i": { fontSize: tokens.typography.fontSize["md-12px"] },
    "&:hover:not(:disabled)": { borderColor: tokens.colors.brand.blue["500"] },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
}));

const StyledResetButton = styled.button({
    height: "35px",
    padding: `0 ${tokens.globals["spacing-2-5"]}`,
    background: tokens.colors.neutral.black,
    boxShadow: tokens.effects.effects.shadow.default,
    borderRadius: tokens.globals["border-radius"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.neutral.black}`,
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    cursor: "pointer",
    color: tokens.colors.neutral.white,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    whiteSpace: "nowrap",

    "i": { fontSize: tokens.typography.fontSize["md-12px"] },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

const StyledExportGroup = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-3"],
    flexShrink: 0,
});

const StyledExportButton = styled.button({
    width: tokens.globals["spacing-8"],
    height: tokens.globals["spacing-8"],
    padding: 0,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colors.text.primary,
    cursor: "pointer",
    borderRadius: tokens.globals["border-radius"],

    "i": { fontSize: tokens.typography.fontSize["lg-14px"] },

    "&:hover:not(:disabled)": {
        background: tokens.colors.neutral.grey.Alpha["10"],
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

/**
 * Reusable AIE filter/search toolbar: search input, filter dropdown buttons, reset, extra controls, exports.
 * @param {Function} [props.onSearchChange] Called with `(value, event)` on search input change.
 * @param {Array<object>} [props.filters] Filter button definitions; pass `component` to render a custom control.
 * @param {Function} [props.onReset] Called on reset click; button hidden when omitted.
 * @param {Array<object>} [props.exportOptions] Icon-only export buttons, rendered inline after Reset.
 * @param {React.ReactNode} [props.rightContent] Extra control(s) pinned to the far right of the bar.
 * Also: searchValue, searchPlaceholder, searchAriaLabel, resetLabel, disabled, disabledReason, className, children (self-explanatory).
 */
function FilterBar({
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search",
    searchAriaLabel = "Search",
    filters = [],
    onReset,
    resetLabel = "Reset",
    exportOptions = [],
    disabled = false,
    disabledReason,
    className,
    children,
    rightContent,
}) {
    return (
        <StyledBar className={className} aria-busy={disabled || undefined}>
            <StyledRow>
                <StyledSearch $disabled={disabled} title={disabled ? disabledReason : undefined}>
                    {searchValue ? (
                        <StyledSearchClearButton
                            type="button"
                            disabled={disabled}
                            aria-label="Clear search"
                            onClick={(event) => onSearchChange && onSearchChange("", event)}
                        >
                            <i className="fal fa-times" aria-hidden="true" />
                        </StyledSearchClearButton>
                    ) : (
                        <StyledSearchIcon className="fal fa-search" aria-hidden="true" />
                    )}
                    <StyledSearchInput
                        placeholder={searchPlaceholder}
                        aria-label={searchAriaLabel}
                        value={searchValue}
                        disabled={disabled}
                        onChange={(event) => onSearchChange && onSearchChange(event.target.value, event)}
                    />
                </StyledSearch>

                {filters.map((filter, index) => {
                    if (filter.component) {
                        return (
                            <React.Fragment key={filter.key || index}>
                                {filter.component}
                            </React.Fragment>
                        );
                    }

                    const isDisabled = disabled || Boolean(filter.disabled);
                    return (
                        <StyledFilterButton
                            key={filter.key || index}
                            type="button"
                            onClick={filter.onClick}
                            disabled={isDisabled}
                            $active={Boolean(filter.active)}
                            aria-pressed={filter.active === undefined ? undefined : Boolean(filter.active)}
                            title={isDisabled ? (filter.disabledReason || disabledReason) : filter.title}
                        >
                            {filter.icon && <i className={`fal fa-${filter.icon}`} aria-hidden="true" />}
                            <span>{filter.label}</span>
                            <i className="fal fa-chevron-down" aria-hidden="true" />
                        </StyledFilterButton>
                    );
                })}

                {children}

                {onReset && (
                    <StyledResetButton
                        type="button"
                        onClick={onReset}
                        disabled={disabled}
                        title={disabled ? disabledReason : undefined}
                    >
                        <span>{resetLabel}</span>
                        <i className="fal fa-undo" aria-hidden="true" />
                    </StyledResetButton>
                )}

                {exportOptions.length > 0 && (
                    <StyledExportGroup>
                        {exportOptions.map((option, index) => {
                            const isDisabled = disabled || Boolean(option.disabled);
                            return (
                                <StyledExportButton
                                    key={option.key || index}
                                    type="button"
                                    onClick={option.onClick}
                                    disabled={isDisabled}
                                    aria-label={option.ariaLabel || option.label || option.key || "Export"}
                                    title={isDisabled ? (option.disabledReason || disabledReason) : option.title}
                                >
                                    {option.icon && <i className={`fal fa-${option.icon}`} aria-hidden="true" />}
                                </StyledExportButton>
                            );
                        })}
                    </StyledExportGroup>
                )}
            </StyledRow>

            {rightContent && (
                <StyledExportGroup>
                    {rightContent}
                </StyledExportGroup>
            )}
        </StyledBar>
    );
}

FilterBar.displayName = "FilterBar";

export default FilterBar;
